import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Close,
  ConfirmationNumber,
  HelpOutline,
  HourglassEmpty,
  KeyboardArrowRight,
  ReportProblem,
  Refresh,
  Schedule,
  Send,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  createTicket,
  getUserTickets,
  getTicketById,
  addMessage,
} from '../utils/ticketManager';

const CATEGORIES = [
  'Transfers',
  'Deposits',
  'SMS & Messaging',
  'API & Integration',
  'Account & Security',
  'Other',
];

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_CONFIG = {
  Open: { color: 'info', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  'In Progress': { color: 'warning', icon: <Schedule sx={{ fontSize: 14 }} /> },
  Resolved: { color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  Closed: { color: 'default', icon: <Close sx={{ fontSize: 14 }} /> },
};

const PRIORITY_CONFIG = {
  Low: { color: 'default' },
  Medium: { color: 'info' },
  High: { color: 'warning' },
  Urgent: { color: 'error' },
};

function TicketConversation({ ticket, onClose, onUpdated }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setSendError(null);
    try {
      const updated = await addMessage(ticket.id, replyText.trim(), false);
      onUpdated(updated);
      setReplyText('');
    } catch (err) {
      setSendError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend();
  };

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.Open;

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography fontWeight={700} fontSize={16}>
                {ticket.subject}
              </Typography>
              <Chip
                label={ticket.ticketNumber}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, fontWeight: 600 }}
              />
            </Stack>
            <Stack direction="row" gap={1} mt={0.5}>
              <Chip
                icon={statusCfg.icon}
                label={ticket.status}
                size="small"
                color={statusCfg.color}
              />
              <Chip label={ticket.category} size="small" variant="outlined" />
              <Chip
                label={ticket.priority}
                size="small"
                color={PRIORITY_CONFIG[ticket.priority]?.color || 'default'}
                variant="outlined"
              />
            </Stack>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxHeight: 420,
          overflowY: 'auto',
        }}
      >
        {ticket.messages.map((msg) => {
          const isUser = msg.sender === 'user';
          return (
            <Box
              key={msg.id}
              sx={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
              }}
            >
              <Box sx={{ maxWidth: '75%' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.4, textAlign: isUser ? 'right' : 'left' }}
                >
                  {msg.senderName} · {format(new Date(msg.timestamp), 'MMM d, HH:mm')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.75,
                    py: 1.25,
                    borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: isUser
                      ? '#0BA480'
                      : isDark
                      ? 'rgba(255,255,255,0.06)'
                      : '#F1F5F9',
                    color: isUser ? '#fff' : 'text.primary',
                    border: isUser ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                  }}
                >
                  <Typography fontSize={14} lineHeight={1.6} sx={{ whiteSpace: 'pre-wrap' }}>
                    {msg.message}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      {ticket.status !== 'Closed' && ticket.status !== 'Resolved' && (
        <>
          <Divider />
          {sendError && (
            <Alert severity="error" sx={{ mx: 2, mt: 1.5, borderRadius: '8px' }}>
              {sendError}
            </Alert>
          )}
          <DialogActions sx={{ p: 2, gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Type your reply... (Ctrl+Enter to send)"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={handleKey}
              size="small"
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              endIcon={<Send />}
              onClick={handleSend}
              disabled={!replyText.trim() || sending}
              sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#059669' }, flexShrink: 0 }}
            >
              Send
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

function NewTicketDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    subject: '',
    category: '',
    description: '',
    priority: 'Medium',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.category) e.category = 'Category is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.description.trim().length < 20)
      e.description = 'Please provide more detail (at least 20 characters)';
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const ticket = await createTicket(form);
      setSuccess(ticket);
      onCreated(ticket);
    } catch (err) {
      setErrors({ submit: err?.response?.data?.detail || 'Failed to submit ticket. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm({ subject: '', category: '', description: '', priority: 'Medium' });
    setErrors({});
    setSuccess(null);
    onClose();
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-focused fieldset': { borderColor: '#0BA480' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#0BA480' },
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={700}>Submit a Support Ticket</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />

      {success ? (
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 56, color: '#0BA480', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={700} mb={0.5}>
            Ticket Submitted!
          </Typography>
          <Typography color="text.secondary" mb={2}>
            Your ticket number is
          </Typography>
          <Chip
            label={success.ticketNumber}
            sx={{
              fontSize: 20,
              fontWeight: 800,
              height: 44,
              px: 2,
              bgcolor: '#F0FDF4',
              color: '#0BA480',
              border: '1.5px solid #0BA480',
            }}
          />
          <Typography color="text.secondary" mt={2} fontSize={14}>
            Our support team will review and respond shortly.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ mt: 3, bgcolor: '#0BA480', '&:hover': { bgcolor: '#059669' } }}
          >
            Done
          </Button>
        </DialogContent>
      ) : (
        <>
          <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
            <TextField
              label="Subject"
              placeholder="Brief summary of your issue"
              value={form.subject}
              onChange={handleChange('subject')}
              error={!!errors.subject}
              helperText={errors.subject}
              fullWidth
              sx={inputSx}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Category"
                value={form.category}
                onChange={handleChange('category')}
                error={!!errors.category}
                helperText={errors.category}
                fullWidth
                sx={inputSx}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Priority"
                value={form.priority}
                onChange={handleChange('priority')}
                fullWidth
                sx={inputSx}
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Description"
              placeholder="Please describe your issue in detail..."
              value={form.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description || `${form.description.length} characters`}
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#059669' } }}
            >
              Submit Ticket
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

const Support = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserTickets();
      setTickets(data);
    } catch (err) {
      setError('Failed to load your tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreated = (ticket) => {
    refresh();
    setNewTicketOpen(false);
    setSelectedTicket(ticket);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setSelectedTicket(updatedTicket);
    refresh();
  };

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const fresh = await getTicketById(ticket.id);
      setSelectedTicket(fresh);
    } catch (err) {
      // Keep showing the cached ticket if the refetch fails
    }
  };

  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'Open').length,
    inProgress: tickets.filter((t) => t.status === 'In Progress').length,
    resolved: tickets.filter((t) => t.status === 'Resolved' || t.status === 'Closed').length,
  };

  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <HelpOutline sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Support Center
            </Typography>
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Submit a ticket and our team will get back to you as soon as possible.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewTicketOpen(true)}
            sx={{ bgcolor: '#0BA480', '&:hover': { bgcolor: '#059669' }, borderRadius: '8px' }}
          >
            {isMobile ? 'New' : 'New Ticket'}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Tickets', value: stats.total, color: 'text.primary' },
          { label: 'Open', value: stats.open, color: '#3B82F6' },
          { label: 'In Progress', value: stats.inProgress, color: '#F59E0B' },
          { label: 'Resolved', value: stats.resolved, color: '#0BA480' },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card
              elevation={0}
              sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}
            >
              <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {s.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={s.color}>
                  {s.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tickets List */}
      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${borderClr}` }}>
          <Typography fontWeight={600} fontSize={15}>
            My Tickets
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 2.5 }}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5, borderRadius: '10px' }} />
            ))}
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <ConfirmationNumber sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography color="text.secondary" mb={1}>
              You haven't submitted any tickets yet.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setNewTicketOpen(true)}
              sx={{
                borderColor: '#0BA480',
                color: '#0BA480',
                '&:hover': { borderColor: '#059669', bgcolor: 'rgba(11,164,128,0.05)' },
              }}
            >
              Submit Your First Ticket
            </Button>
          </Box>
        ) : (
          <Box>
            {tickets.map((ticket, idx) => {
              const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.Open;
              const lastMsg = ticket.messages[ticket.messages.length - 1];
              const hasAdminReply = ticket.messages.some((m) => m.sender === 'admin');

              return (
                <Box
                  key={ticket.id}
                  onClick={() => handleOpenTicket(ticket)}
                  sx={{
                    px: 2.5,
                    py: 2,
                    cursor: 'pointer',
                    borderBottom:
                      idx < tickets.length - 1 ? `1px solid ${borderClr}` : 'none',
                    transition: 'background 0.15s',
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction={isMobile ? 'column' : 'row'}
                      alignItems={isMobile ? 'flex-start' : 'center'}
                      gap={1}
                      mb={0.5}
                    >
                      <Typography fontWeight={600} fontSize={14} noWrap>
                        {ticket.subject}
                      </Typography>
                      <Stack direction="row" gap={0.75} flexShrink={0}>
                        <Chip
                          label={ticket.ticketNumber}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11, fontWeight: 600, height: 20 }}
                        />
                        <Chip
                          icon={statusCfg.icon}
                          label={ticket.status}
                          size="small"
                          color={statusCfg.color}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                        {hasAdminReply && (
                          <Chip
                            label="Replied"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                      </Stack>
                    </Stack>
                    <Typography
                      fontSize={13}
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: { xs: '100%', sm: '80%' } }}
                    >
                      {lastMsg?.message}
                    </Typography>
                    <Stack direction="row" gap={1.5} mt={0.5}>
                      <Typography fontSize={12} color="text.disabled">
                        {ticket.category}
                      </Typography>
                      <Typography fontSize={12} color="text.disabled">
                        ·
                      </Typography>
                      <Typography fontSize={12} color="text.disabled">
                        {format(new Date(ticket.createdAt), 'MMM d, yyyy HH:mm')}
                      </Typography>
                      <Typography fontSize={12} color="text.disabled">
                        ·
                      </Typography>
                      <Typography fontSize={12} color="text.disabled">
                        {ticket.messages.length} message{ticket.messages.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                  </Box>
                  <KeyboardArrowRight sx={{ color: 'text.disabled', flexShrink: 0 }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      {/* Info banner */}
      <Alert
        severity="info"
        icon={<ReportProblem />}
        sx={{ mt: 2, borderRadius: '10px' }}
      >
        For urgent issues, you can also reach us at{' '}
        <strong>support@zenopay.net</strong> or use the live chat widget.
      </Alert>

      {/* Dialogs */}
      <NewTicketDialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        onCreated={handleCreated}
      />
      {selectedTicket && (
        <TicketConversation
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={handleTicketUpdated}
        />
      )}
    </Container>
  );
};

export default Support;
